import {
  AdminUserDetail,
  LeadTag,
  LeadTagMatch,
  LeadsListParams,
} from 'models/Lead';

import { normalizeTagName } from 'utils/LeadTagHelper';

/** Tags shown inline in the grid cell before collapsing into "+N". */
export const GRID_VISIBLE_TAGS = 2;

export const splitVisibleTags = (
  tags: LeadTag[] | undefined,
  max: number = GRID_VISIBLE_TAGS
): { visible: LeadTag[]; hidden: LeadTag[] } => {
  const all = tags ?? [];
  return { visible: all.slice(0, max), hidden: all.slice(max) };
};

interface ActiveFilterGroups {
  statuses: unknown[];
  universities: unknown[];
  region?: string;
  cities: unknown[];
  tagIds: number[];
  nationalities?: number[];
  genders?: number[];
  maritalStatuses?: number[];
  // "Datos de pago": 'true' | 'false'; unset means "Todos".
  payment?: string;
}

/** Country always counts (it is required); each other group counts once. */
export const countActiveFilters = (f: ActiveFilterGroups): number =>
  1 +
  (f.statuses.length > 0 ? 1 : 0) +
  (f.universities.length > 0 ? 1 : 0) +
  (f.region ? 1 : 0) +
  (f.cities.length > 0 ? 1 : 0) +
  (f.tagIds.length > 0 ? 1 : 0) +
  ((f.nationalities ?? []).length > 0 ? 1 : 0) +
  ((f.genders ?? []).length > 0 ? 1 : 0) +
  ((f.maritalStatuses ?? []).length > 0 ? 1 : 0) +
  (f.payment ? 1 : 0);

/** `tagMatch` only matters (and is only sent) when there are tags. */
export const applyTagFilterParams = <T extends object>(
  params: T,
  tagIds: number[],
  tagMatch: LeadTagMatch
): T & Pick<LeadsListParams, 'tagIds' | 'tagMatch'> => {
  if (tagIds.length === 0) return params;
  return { ...params, tagIds, tagMatch };
};

export interface BulkPreviewItem {
  name: string;
  isNew: boolean;
  assignedCount: number;
  alreadyHadCount: number;
}

/**
 * Per chosen tag: how many of the selected leads will get it and how many
 * already had it. `catalogNames` are the names known to exist in the country.
 */
export const computeBulkPreview = (
  rowsTags: (LeadTag[] | undefined)[],
  chosen: string[],
  catalogNames: string[]
): BulkPreviewItem[] => {
  const catalog = new Set(catalogNames.map(normalizeTagName));
  return chosen.map((name) => {
    const key = normalizeTagName(name);
    const alreadyHadCount = rowsTags.filter((tags) =>
      (tags ?? []).some((tag) => normalizeTagName(tag.name) === key)
    ).length;
    return {
      name,
      isNew: !catalog.has(key),
      assignedCount: rowsTags.length - alreadyHadCount,
      alreadyHadCount,
    };
  });
};

/**
 * How many of the selected leads will actually gain something: those missing
 * at least one of the chosen tags. The rest already had every chosen tag.
 */
export const countLeadsGainingTags = (
  rowsTags: (LeadTag[] | undefined)[],
  chosen: string[]
): number => {
  const chosenKeys = chosen.map(normalizeTagName);
  return rowsTags.filter((tags) => {
    const owned = new Set(
      (tags ?? []).map((tag) => normalizeTagName(tag.name))
    );
    return chosenKeys.some((key) => !owned.has(key));
  }).length;
};

/**
 * Applies freshly saved tags to the open detail only if the save belongs to
 * the lead still on screen (a save can resolve after the user navigated).
 */
export const applySavedLeadTags = (
  detail: AdminUserDetail | null,
  savedLeadId: string,
  currentLeadId: string,
  tags: LeadTag[]
): AdminUserDetail | null => {
  if (!detail || savedLeadId !== currentLeadId) return detail;
  return { ...detail, tags };
};

/** Applied tag filter: `tagMatch` only applies when there are tags. */
export const buildAppliedTagFilter = (
  draftTags: number[],
  draftTagMatch: LeadTagMatch
): { tagIds: number[]; tagMatch: LeadTagMatch } => ({
  tagIds: draftTags,
  tagMatch: draftTags.length > 0 ? draftTagMatch : 'any',
});

interface CountryScopedDraft {
  university: (number | null)[] | number[];
  region: string | undefined;
  cities: number[];
  tags: number[];
  genders?: number[];
  maritalStatuses?: number[];
}

/**
 * Catalogs (universities, regions, cities, tags, genders, marital statuses)
 * belong to one country: when the draft country changes, whatever was chosen
 * no longer exists. Nationalities are global, so they are kept.
 */
export const clearCountryScopedDraft = <T extends CountryScopedDraft>(
  draft: T
): T => ({
  ...draft,
  university: [],
  region: undefined,
  cities: [],
  tags: [],
  genders: [],
  maritalStatuses: [],
});
